import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import {
  Box,
  Checkbox,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductActionId, RegistryProduct } from '@/types/productRegistry';
import { ProductActionsMenu } from './ProductActionsMenu';
import { ProductEnvironmentChip } from './EnvironmentChip';
import { ProductStatusChip } from './ProductStatusChip';

interface ProductTableRowProps {
  product: RegistryProduct;
  selected: boolean;
  onSelect: (productId: string, selected: boolean) => void;
  onAction: (actionId: ProductActionId, product: RegistryProduct) => void;
}

const iconMap: Record<string, SvgIconComponent> = {
  inventory: Inventory2OutlinedIcon,
  monitoring: MonitorHeartOutlinedIcon,
  compliance: GppGoodOutlinedIcon,
  database: StorageOutlinedIcon,
  identity: PersonOutlineOutlinedIcon,
  description: DescriptionOutlinedIcon,
  audit: DescriptionOutlinedIcon,
  flag: FlagOutlinedIcon,
};

function formatActiveUsers(value: number): string {
  return value.toLocaleString();
}

function formatLastModified(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const ProductTableRow = memo(function ProductTableRow({
  product,
  selected,
  onSelect,
  onAction,
}: ProductTableRowProps) {
  const { t } = useTranslation('entity-portal');
  const Icon = iconMap[product.icon ?? 'inventory'] ?? AppsOutlinedIcon;

  return (
    <TableRow
      hover
      selected={selected}
      aria-selected={selected}
      sx={{
        '&.Mui-selected': {
          bgcolor: 'action.selected',
        },
        '& .MuiTableCell-root': {
          py: 1.25,
        },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={(event) => onSelect(product.id, event.target.checked)}
          inputProps={{
            'aria-label': t('productRegistry.productRegistrySelectRow', {
              product: t(product.name, { defaultValue: product.name }),
            }),
          }}
        />
      </TableCell>

      <TableCell>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography variant="subtitle1" noWrap>
            {t(product.name, { defaultValue: product.name })}
          </Typography>
        </Stack>
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {t(`productRegistry.productRegistryCategories.${product.category}`, {
            defaultValue: product.category,
          })}
        </Typography>
      </TableCell>

      <TableCell>
        <ProductStatusChip
          status={product.status}
          label={t(`productRegistry.productRegistryStatus.${product.status}`)}
        />
      </TableCell>

      <TableCell>
        <ProductEnvironmentChip
          environment={product.environment}
          label={t(`productRegistry.productRegistryEnvironment.${product.environment}`)}
        />
      </TableCell>

      <TableCell align="right">
        <Typography variant="body2">
          {formatActiveUsers(product.activeUsers)}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatLastModified(product.lastModified)}
        </Typography>
      </TableCell>

      <TableCell align="right">
        <ProductActionsMenu product={product} onAction={onAction} />
      </TableCell>
    </TableRow>
  );
});

type ProductCardRowProps = ProductTableRowProps;

export const ProductCardRow = memo(function ProductCardRow({
  product,
  selected,
  onSelect,
  onAction,
}: ProductCardRowProps) {
  const { t } = useTranslation('entity-portal');
  const Icon = iconMap[product.icon ?? 'inventory'] ?? AppsOutlinedIcon;

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        bgcolor: selected ? 'action.selected' : 'background.paper',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Checkbox
            checked={selected}
            onChange={(event) => onSelect(product.id, event.target.checked)}
          />
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography variant="subtitle1">
            {t(product.name, { defaultValue: product.name })}
          </Typography>
        </Stack>
        <ProductActionsMenu product={product} onAction={onAction} />
      </Stack>

      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            {t('productRegistry.productRegistryColumnCategory')}
          </Typography>
          <Typography variant="caption">
            {t(`productRegistry.productRegistryCategories.${product.category}`, {
              defaultValue: product.category,
            })}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="caption" color="text.secondary">
            {t('productRegistry.productRegistryColumnStatus')}
          </Typography>
          <ProductStatusChip
            status={product.status}
            label={t(`productRegistry.productRegistryStatus.${product.status}`)}
          />
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="caption" color="text.secondary">
            {t('productRegistry.productRegistryColumnEnvironment')}
          </Typography>
          <ProductEnvironmentChip
            environment={product.environment}
            label={t(`productRegistry.productRegistryEnvironment.${product.environment}`)}
          />
        </Stack>
      </Stack>
    </Box>
  );
});
